import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, formatPercent, PAYMENT_METHOD_LABELS, PAYMENT_STATUS_LABELS, formatQuantity, SALES_INVOICE_KIND_LABELS, SALES_INVOICE_STATUS_LABELS, taxCodeByCode, TAX_MODE_LABELS, type PaymentMethod, type SalesInvoice, type SalesInvoiceKind } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CardLayout, FactBoxPart, FactRow } from '../../components/CardLayout';
import { RecordHistory, type LogLabels } from '../../components/RecordHistory';
import { useSession } from '../../session';
import { serviceRange } from '../orders/OrdersPage';
import { InvoicePayments } from './InvoicePayments';
import { InvoicePaymentLinks } from './InvoicePaymentLinks';
import { invoiceBadge, invoiceDay } from './InvoicesPage';
import '../offers/offers.css';
import './invoices.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/** Betrag als „ über 109,00 €“, wenn er im Eintrag steht (ohne das Recht, Beträge zu sehen, fehlt er) */
const over = (d: Record<string, unknown>, key = 'amountCents') => (typeof d[key] === 'number' ? ` über ${formatCents(d[key] as number)}` : '');
const PROVIDER = (p: unknown) => (p === 'stripe' ? 'Stripe' : p === 'paypal' ? 'PayPal' : String(p));

/** Sätze für den Verlauf der Rechnung, aus den Aktionen im Änderungsprotokoll. Einträge der Zahlungslinks beginnen mit „link_“. */
const SALES_LOG: LogLabels = {
  create: (d) => (d.related ? `Als Entwurf zur Rechnung ${d.related} angelegt` : 'Als Entwurf angelegt'),
  update: () => 'Entwurf geändert',
  delete: () => 'Entwurf gelöscht',
  issue: (d) => `Ausgestellt als ${d.number ?? 'Rechnung'}${over(d, 'grossCents')}`,
  payment_links_auto: (d) => `Zahlungslink für ${(Array.isArray(d.providers) ? d.providers : []).map(PROVIDER).join(' und ')} beim Ausstellen angelegt, als QR-Code in der Rechnung`,
  mail: (d) => `Per E-Mail an ${d.to ?? 'den Kunden'} gesendet${d.via === 'ride_completed' ? ', automatisch nach der Fahrt' : ''}${d.withLink ? ', mit Zahlungslink' : ''}`,
  payment: (d) => `Zahlung${over(d)} erfasst${d.method ? ` (${PAYMENT_METHOD_LABELS[d.method as PaymentMethod] ?? d.method})` : ''}`,
  payment_reverse: (d) => `Zahlung${over(d)} zurückgenommen`,
  correction: (d) => `${SALES_INVOICE_KIND_LABELS[d.kind as SalesInvoiceKind] ?? 'Korrektur'} ${d.number ?? ''} ausgestellt${over(d, 'grossCents')}`.replace('  ', ' '),
  link_create: (d) => `Zahlungslink für ${PROVIDER(d.provider)} angelegt${over(d)}`,
  link_cancel: () => 'Zahlungslink zurückgezogen',
  link_send: (d) => `Zahlungslink an ${d.to ?? 'den Kunden'} gesendet`,
  link_paid: (d) => `Online bezahlt${over(d)}`,
  link_paid_mail: (d) => `Zahlungsbestätigung an ${d.to ?? 'den Kunden'} gesendet`,
};

/**
 * Ansicht einer Ausgangsrechnung (KON-151): Kopf mit Nummer und Stand, Empfänger, Positionen mit Summen und die Infobox. Entwürfe lassen sich bearbeiten,
 * löschen und ausstellen, ausgestellte Rechnungen nur ansehen und herunterladen. Was erlaubt ist, entscheidet der Server, die Seite blendet nur aus, was nicht passt.
 */
export function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const invoice = useQuery({ queryKey: ['invoice-out', id], queryFn: async () => (await api<{ data: SalesInvoice }>('GET', `/invoices-out/${id}`)).data });

  const issue = useMutation({
    mutationFn: async () => (await api<{ data: SalesInvoice }>('POST', `/invoices-out/${id}/issue`)).data,
    onSuccess: (i) => {
      client.setQueryData(['invoice-out', i.id], i);
      client.invalidateQueries({ queryKey: ['invoices-out'] });
      client.invalidateQueries({ queryKey: ['order'] });
      client.invalidateQueries({ queryKey: ['orders'] });
      setError(null);
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setError(errorText(e, 'Die Rechnung konnte nicht ausgestellt werden.'));
    },
  });
  const [correcting, setCorrecting] = useState<'cancel' | 'credit' | null>(null);
  const [reason, setReason] = useState('');
  const correct = useMutation({
    mutationFn: async (kind: 'cancel' | 'credit') => (await api<{ data: SalesInvoice }>('POST', `/invoices-out/${id}/${kind === 'cancel' ? 'cancel' : 'credit-note'}`, { reason: reason.trim() })).data,
    onSuccess: (created, kind) => {
      client.invalidateQueries({ queryKey: ['invoices-out'] });
      client.invalidateQueries({ queryKey: ['invoice-out'] });
      client.invalidateQueries({ queryKey: ['open-balance'] });
      client.invalidateQueries({ queryKey: ['contact-stats'] });
      setCorrecting(null);
      setReason('');
      navigate(kind === 'credit' ? `/rechnungen/${created.id}/bearbeiten` : `/rechnungen/${created.id}`);
    },
    onError: (e) => setError(errorText(e, 'Das hat nicht geklappt.')),
  });
  // Versand per E-Mail: Empfänger ist vorbelegt mit der Adresse des Kunden, ein eigener Text ist freiwillig
  const [sending, setSending] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: async () => (await api<{ data: { sentTo: string } }>('POST', `/invoices-out/${id}/send`, { ...(sendTo.trim() ? { to: sendTo.trim() } : {}), ...(sendMessage.trim() ? { message: sendMessage.trim() } : {}) })).data,
    onSuccess: (r) => {
      setSending(false);
      setSendMessage('');
      setError(null);
      setNotice(`Die Rechnung ist per E-Mail an ${r.sentTo} unterwegs.`);
      client.invalidateQueries({ queryKey: ['invoice-log', id] });
    },
    onError: (e) => setError(errorText(e, 'Die E-Mail konnte nicht gesendet werden.')),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/invoices-out/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['invoices-out'] });
      client.removeQueries({ queryKey: ['invoice-out', id] });
      navigate('/rechnungen', { replace: true });
    },
    onError: (e) => setError(errorText(e, 'Der Entwurf konnte nicht gelöscht werden.')),
  });

  if (invoice.isLoading) return <p className="muted">Wird geladen …</p>;
  if (invoice.isError || !invoice.data) {
    return (
      <div className="card narrow">
        <h1>Rechnung nicht gefunden</h1>
        <Link to="/rechnungen">Zur Liste</Link>
      </div>
    );
  }
  const i = invoice.data;
  const draft = i.status === 'draft';
  const r = i.recipient;
  const amounts = !i.amountsHidden;
  const small = i.taxMode === 'small_business';
  const correctable = i.status === 'issued' && ['invoice', 'partial', 'final'].includes(i.kind) && can('invoices_out.cancel') && !i.amountsHidden;
  const cancellation = i.corrections.find((c) => c.kind === 'cancellation' && c.status === 'issued');
  const creditDraft = i.corrections.find((c) => c.kind === 'credit_note' && c.status === 'draft');
  const hasCredit = i.corrections.some((c) => c.kind === 'credit_note' && c.status === 'issued');
  const draftTitle = i.kind === 'credit_note' ? 'Gutschriftsentwurf' : i.kind === 'partial' ? 'Entwurf der Anzahlungsrechnung' : i.kind === 'final' ? 'Entwurf der Schlussrechnung' : 'Rechnungsentwurf';
  const issueLabel = i.kind === 'credit_note' ? 'Gutschrift ausstellen' : i.kind === 'partial' ? 'Anzahlungsrechnung ausstellen' : i.kind === 'final' ? 'Schlussrechnung ausstellen' : 'Rechnung ausstellen';
  const badge = invoiceBadge({ status: i.status, paymentStatus: i.settlement?.status ?? null, overdue: i.settlement?.overdueDays != null });
  const notes = [...new Set(i.lines.map((l) => taxCodeByCode(l.taxCode)).filter((c) => c?.note).map((c) => c!))].map((c) => (i.exemptionNotes[c.category] ? `${c.note} ${i.exemptionNotes[c.category]}` : c.note));

  return (
    <>
      <p><Link to="/rechnungen" className="back-link">← Alle Rechnungen</Link></p>
      <div className="page-head">
        <h1>{i.number ?? draftTitle}</h1>
        <span className={badge.className}>{badge.text}</span>
        <span className="muted">{SALES_INVOICE_KIND_LABELS[i.kind]}{i.subject ? ` · ${i.subject}` : ''}</span>
      </div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--ok" role="status">{notice}</div>}
      {!draft && <div className="alert alert--ok">Ausgestellt{i.issuedAt ? ` am ${stamp.format(new Date(i.issuedAt))} Uhr` : ''}{i.issuedByName ? ` von ${i.issuedByName}` : ''}. Die Rechnung ist festgeschrieben und lässt sich nicht mehr ändern.</div>}
      {cancellation && <div className="alert alert--error">Storniert mit der Stornorechnung <Link to={`/rechnungen/${cancellation.id}`}>{cancellation.number}</Link>.</div>}
      {i.relatedInvoiceId && <div className="alert">Bezieht sich auf die Rechnung <Link to={`/rechnungen/${i.relatedInvoiceId}`}>{i.relatedInvoiceNumber}</Link>. Der Betrag wird mit ihr verrechnet.</div>}
      {i.amountsHidden && <div className="alert">Beträge, PDF und E-Rechnung sehen nur Benutzer mit dem Recht „Beträge sehen“.</div>}

      <CardLayout factbox={<InvoiceFactBox invoice={i} />}>
        <div className="card">
          <div className="invoice-actions">
            {draft && can('invoices_out.write') && <Link className="btn btn--secondary btn--sm" to={`/rechnungen/${i.id}/bearbeiten`}>Bearbeiten</Link>}
            {draft && can('invoices_out.issue') && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={issue.isPending || !amounts}
                onClick={() => window.confirm('Rechnung jetzt ausstellen? Sie bekommt ihre Rechnungsnummer und ist danach festgeschrieben. Das lässt sich nicht rückgängig machen.') && issue.mutate()}
              >
                {issue.isPending ? 'Wird ausgestellt …' : issueLabel}
              </button>
            )}
            {amounts && <a className="btn btn--secondary btn--sm" href={`/api/v1/invoices-out/${i.id}/pdf`}>{draft ? 'PDF-Vorschau' : 'PDF herunterladen'}</a>}
            {amounts && !draft && i.xmlDocumentId && <a className="btn btn--secondary btn--sm" href={`/api/v1/invoices-out/${i.id}/xml`}>E-Rechnung (XML)</a>}
            {amounts && !draft && can('invoices_out.issue') && (
              <button type="button" className="btn btn--secondary btn--sm" aria-expanded={sending} onClick={() => { setSending((v) => !v); setSendTo((cur) => cur || r.email || ''); setError(null); setNotice(null); }}>Per E-Mail senden …</button>
            )}
            {correctable && !cancellation && !creditDraft && (
              <>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setCorrecting(correcting === 'credit' ? null : 'credit'); setError(null); }}>Gutschrift erstellen …</button>
                {!hasCredit && <button type="button" className="btn btn--danger btn--sm" onClick={() => { setCorrecting(correcting === 'cancel' ? null : 'cancel'); setError(null); }}>Stornieren …</button>}
              </>
            )}
            {creditDraft && <Link className="btn btn--secondary btn--sm" to={`/rechnungen/${creditDraft.id}`}>Gutschriftsentwurf öffnen</Link>}
            {draft && can('invoices_out.delete') && (
              <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm('Diesen Entwurf löschen?') && remove.mutate()}>Entwurf löschen</button>
            )}
          </div>
        </div>

        {sending && (
          <form
            className="card payment-form"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              send.mutate();
            }}
          >
            <h2>Rechnung per E-Mail senden</h2>
            <p className="muted small">Die Rechnung geht als PDF im Anhang aus dem Postfach Ihres Unternehmens, mit Betrag und Fälligkeit im Text. Gibt es einen offenen Zahlungslink, steht er als Schaltfläche in der E-Mail. Der Versand steht danach im Verlauf.</p>
            <label className="field">
              <span>An</span>
              <input className="input" type="email" required value={sendTo} onChange={(e) => setSendTo(e.target.value)} maxLength={200} autoComplete="email" />
            </label>
            <label className="field">
              <span>Eigener Text (freiwillig)</span>
              <textarea className="input textarea textarea--short" value={sendMessage} onChange={(e) => setSendMessage(e.target.value)} maxLength={2000} placeholder="Zum Beispiel: Vielen Dank für Ihren Auftrag." />
            </label>
            <div className="form-actions">
              <button type="submit" className="btn btn--primary btn--sm" disabled={send.isPending || !sendTo.trim()}>{send.isPending ? 'Wird gesendet …' : 'Senden'}</button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setSending(false)}>Abbrechen</button>
            </div>
          </form>
        )}

        {correcting && (
          <form
            className="card payment-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (correcting === 'cancel' && !reason.trim()) return setError('Bitte geben Sie einen Grund für die Stornierung an.');
              correct.mutate(correcting);
            }}
          >
            <h2>{correcting === 'cancel' ? 'Rechnung stornieren' : 'Gutschrift erstellen'}</h2>
            <p className="muted small">
              {correcting === 'cancel'
                ? 'Wirkia stellt sofort eine Stornorechnung über den vollen Betrag aus. Die Rechnung bleibt unverändert stehen und gilt als ausgeglichen. Das lässt sich nicht rückgängig machen. Hat der Kunde schon bezahlt, bleibt ein Guthaben.'
                : 'Wirkia legt einen Gutschriftsentwurf mit allen Positionen an. Dort kürzen Sie auf den gutzuschreibenden Betrag und stellen ihn aus. Die Gutschrift wird mit dieser Rechnung verrechnet.'}
            </p>
            <label className="field">
              <span>{correcting === 'cancel' ? 'Grund der Stornierung' : 'Grund (freiwillig)'}</span>
              <textarea className="input textarea textarea--short" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
            </label>
            <div className="form-actions">
              <button type="submit" className={`btn btn--sm ${correcting === 'cancel' ? 'btn--danger' : 'btn--primary'}`} disabled={correct.isPending}>{correcting === 'cancel' ? 'Stornorechnung ausstellen' : 'Gutschriftsentwurf anlegen'}</button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setCorrecting(null)}>Abbrechen</button>
            </div>
          </form>
        )}

        <div className="card">
          <div className="offer-head">
            <div>
              <div className="muted small">Rechnungsempfänger</div>
              <Link to={`/kontakte/${i.contactId}`} className="strong-link">{r.name}</Link>
              {r.company && r.company !== r.name && <div>{r.company}</div>}
              <div>{r.street}</div>
              <div>{[r.postalCode, r.city].filter(Boolean).join(' ')}</div>
              {r.email && <div className="muted small">{r.email}</div>}
            </div>
            <dl className="offer-meta">
              <dt>Rechnungsdatum</dt>
              <dd>{invoiceDay(i.issueDate)}</dd>
              <dt>Leistungs Datum</dt>
              <dd>{serviceRange(i.serviceStart, i.serviceEnd)}</dd>
              <dt>Fällig</dt>
              <dd>{invoiceDay(i.dueDate)}</dd>
              {i.relatedInvoiceId && (<><dt>Zu Rechnung</dt><dd><Link to={`/rechnungen/${i.relatedInvoiceId}`}>{i.relatedInvoiceNumber}</Link></dd></>)}
              {i.orderId && (<><dt>Auftrag</dt><dd><Link to={`/auftraege/${i.orderId}`}>{i.orderNumber ?? 'Auftrag'}</Link></dd></>)}
              {i.customerReference && (<><dt>Bestellnr.</dt><dd>{i.customerReference}</dd></>)}
            </dl>
          </div>
          {i.introText && <p className="invoice-text">{i.introText}</p>}
          <InvoiceLinesView invoice={i} />
          {notes.map((n) => <p key={n} className="muted small">{n}</p>)}
          {small && <p className="muted small">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p>}
          {i.closingText && <p className="invoice-text">{i.closingText}</p>}
        </div>

        <InvoicePayments invoice={i} />
        <InvoicePaymentLinks invoice={i} />
        <RecordHistory path={`/invoices-out/${i.id}/log`} queryKey={['invoice-log', i.id, i.status, i.settlement?.openCents ?? null, i.corrections.length]} labels={SALES_LOG} storageKey="invoice-log" />
      </CardLayout>
    </>
  );
}

function InvoiceLinesView({ invoice: i }: { invoice: SalesInvoice }) {
  const small = i.taxMode === 'small_business';
  const t = i.totals;
  return (
    <>
      <table className="table table--cards offer-table">
        <thead>
          <tr>
            <th>Pos.</th>
            <th>Bezeichnung</th>
            <th className="num">Menge</th>
            {!i.amountsHidden && (
              <>
                <th className="num">Einzelpreis</th>
                <th className="num">Rabatt</th>
                <th className="num">USt.</th>
                <th className="num">Betrag</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {i.lines.map((l) => (
            <tr key={l.id}>
              <td data-label="Pos.">{l.position}</td>
              <td>
                <strong>{l.title}</strong>
                {l.description && <div className="muted small offer-desc">{l.description}</div>}
              </td>
              <td data-label="Menge" className="num">{formatQuantity(l.quantityMilli)} {l.unit}</td>
              {!i.amountsHidden && (
                <>
                  <td data-label="Einzelpreis" className="num">{formatCents(l.unitPriceCents)}</td>
                  <td data-label="Rabatt" className="num">{l.discountBp ? formatPercent(l.discountBp) : '–'}</td>
                  <td data-label="USt." className="num">{small ? '–' : l.taxRate > 0 ? `${l.taxRate} %` : (taxCodeByCode(l.taxCode)?.category ?? '0') === 'S' ? '0 %' : 'frei'}</td>
                  <td data-label="Betrag" className="num">{formatCents(l.amountCents)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!i.amountsHidden && (
        <dl className="offer-sum">
          {t.discountCents !== 0 && (
            <>
              <dt>Summe der Positionen</dt>
              <dd>{formatCents(t.linesCents)}</dd>
              <dt>abzüglich {formatPercent(i.discountBp)} Rabatt</dt>
              <dd>– {formatCents(t.discountCents)}</dd>
            </>
          )}
          <dt>Netto</dt>
          <dd>{formatCents(t.netCents)}</dd>
          {!small &&
            t.groups.filter((g) => g.rate > 0).map((g) => (
              <div key={`${g.category}${g.rate}`} className="offer-sum__row">
                <dt>{g.rate} % USt. auf {formatCents(g.netCents)}</dt>
                <dd>{formatCents(g.taxCents)}</dd>
              </div>
            ))}
          <dt className="offer-totals__gross">Gesamt brutto</dt>
          <dd className="offer-totals__gross">{formatCents(t.grossCents)}</dd>
          {t.prepaidCents !== 0 && (
            <>
              {i.prepayments.length > 0 ? (
                i.prepayments.map((p) => (
                  <div key={p.id} className="offer-sum__row">
                    <dt>abzüglich Anzahlung <Link to={`/rechnungen/${p.id}`}>{p.number}</Link> vom {invoiceDay(p.issueDate)}{!small && p.taxCents ? ` (darin ${formatCents(p.taxCents)} USt.)` : ''}</dt>
                    <dd>– {p.grossCents === null ? '' : formatCents(p.grossCents)}</dd>
                  </div>
                ))
              ) : (
                <>
                  <dt>abzüglich Anzahlungen</dt>
                  <dd>– {formatCents(t.prepaidCents)}</dd>
                </>
              )}
              <dt className="offer-totals__gross">Zu zahlen</dt>
              <dd className="offer-totals__gross">{formatCents(t.payableCents)}</dd>
            </>
          )}
        </dl>
      )}
    </>
  );
}

/** Infobox der Rechnung (wie die FactBox in Business Central): Stand, Kunde, Termine, Beträge, Zahlungsbedingungen und Dokumente */
function InvoiceFactBox({ invoice: i }: { invoice: SalesInvoice }) {
  const skonto = i.discountTermsBp && i.discountTermsDays ? `${formatPercent(i.discountTermsBp)} bei Zahlung in ${i.discountTermsDays} Tagen` : null;
  return (
    <>
      <FactBoxPart title="Übersicht" storageKey="invoice-overview">
        <FactRow label="Nummer">{i.number ?? 'noch keine (Entwurf)'}</FactRow>
        <FactRow label="Art">{SALES_INVOICE_KIND_LABELS[i.kind]}</FactRow>
        <FactRow label="Stand">{SALES_INVOICE_STATUS_LABELS[i.status]}</FactRow>
        <FactRow label="Kunde"><Link to={`/kontakte/${i.contactId}`}>{i.contactName}</Link></FactRow>
        <FactRow label="Auftrag">{i.orderId ? <Link to={`/auftraege/${i.orderId}`}>{i.orderNumber ?? 'Auftrag'}</Link> : null}</FactRow>
        <FactRow label="Angelegt">{invoiceDay(i.createdAt.slice(0, 10))}</FactRow>
        <FactRow label="Von">{i.createdByName}</FactRow>
      </FactBoxPart>

      {!i.amountsHidden && (
        <FactBoxPart title="Beträge" storageKey="invoice-amounts">
          <FactRow label="Netto">{formatCents(i.totals.netCents)}</FactRow>
          <FactRow label="Steuer">{formatCents(i.totals.taxCents)}</FactRow>
          <FactRow label="Brutto"><strong>{formatCents(i.totals.grossCents)}</strong></FactRow>
          {i.totals.prepaidCents !== 0 && <FactRow label="Zu zahlen">{formatCents(i.totals.payableCents)}</FactRow>}
        </FactBoxPart>
      )}

      <FactBoxPart title="Zahlung" storageKey="invoice-terms">
        <FactRow label="Fällig">{invoiceDay(i.dueDate)}</FactRow>
        <FactRow label="Zahlungsziel">{`${i.paymentTermsDays} Tage`}</FactRow>
        <FactRow label="Skonto">{skonto}</FactRow>
        {i.settlement && (
          <>
            <FactRow label="Zahlungsstand">{i.settlement.overdueDays != null ? `Überfällig seit ${i.settlement.overdueDays} Tagen` : PAYMENT_STATUS_LABELS[i.settlement.status]}</FactRow>
            <FactRow label="Bezahlt">{formatCents(i.settlement.paidCents)}</FactRow>
            {i.settlement.creditedCents !== 0 && <FactRow label="Verrechnet">{formatCents(i.settlement.creditedCents)}</FactRow>}
            {i.settlement.discountCents !== 0 && <FactRow label="Skonto abgezogen">{formatCents(i.settlement.discountCents)}</FactRow>}
            <FactRow label={i.settlement.openCents < 0 ? 'Guthaben des Kunden' : 'Noch offen'}><strong>{formatCents(Math.abs(i.settlement.openCents))}</strong></FactRow>
          </>
        )}
        <FactRow label="Versteuerung">{TAX_MODE_LABELS[i.taxMode]}</FactRow>
      </FactBoxPart>

      {i.corrections.length > 0 && (
        <FactBoxPart title="Korrekturen" storageKey="invoice-corrections">
          {i.corrections.map((c) => (
            <FactRow key={c.id} label={SALES_INVOICE_KIND_LABELS[c.kind]}>
              <Link to={`/rechnungen/${c.id}`}>{c.number ?? 'Entwurf'}</Link>{c.grossCents !== null ? ` · ${formatCents(c.grossCents)}` : ''}
            </FactRow>
          ))}
        </FactBoxPart>
      )}

      <FactBoxPart title="Dokumente" storageKey="invoice-docs">
        <FactRow label="PDF">{i.pdfDocumentId ? 'erzeugt' : i.status === 'draft' ? 'erst nach dem Ausstellen' : null}</FactRow>
        <FactRow label="E-Rechnung">{i.xmlDocumentId ? (i.einvoiceProfile === 'xrechnung' ? 'XRechnung' : 'ZUGFeRD (EN 16931)') : i.status === 'draft' ? 'erst nach dem Ausstellen' : null}</FactRow>
      </FactBoxPart>
    </>
  );
}
